import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Pagination,
  CircularProgress,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import SeoHead from "../Components/SEO/SeoHead";
import GalleryCard from "../Components/Gallery/GalleryCard";
import GalleryViewPopup from "../Components/Gallery/GalleryViewPopup";
import { BASE_API_URL } from "../config";
import { authFetch } from "../Scripts/authHelper";

const LIMIT = 20;

const Gallery = (props: any) => {
  const { t } = useTranslation();
  const [items, setItems] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState("newest");
  const [timeRange, setTimeRange] = useState("7d");
  const [loading, setLoading] = useState(true);
  const [userVotes, setUserVotes] = useState<Record<number, string>>({});
  const [viewingCaptureId, setViewingCaptureId] = useState<number | null>(null);

  const fetchGallery = useCallback(async () => {
    setLoading(true);
    try {
      const timeParam = timeRange !== "all" ? `&timeRange=${timeRange}` : "";
      const response = await fetch(
        `${BASE_API_URL}/api/gallery?sort=${sort}&page=${page}&limit=${LIMIT}${timeParam}`
      );
      const data = await response.json();
      setItems(data.items || []);
      setTotal(data.total || 0);

      if (props.state.isAuthenticated && data.items?.length > 0) {
        const ids = data.items.map((item: any) => item.Id).join(",");
        const votesResponse = await authFetch(
          `${BASE_API_URL}/api/gallery/my-votes?captureIds=${ids}`
        );
        if (votesResponse.ok) {
          const votesData = await votesResponse.json();
          setUserVotes(votesData.votes || {});
        }
      }
    } catch (err) {
      console.error("Error fetching gallery:", err);
    } finally {
      setLoading(false);
    }
  }, [sort, page, timeRange, props.state.isAuthenticated]);

  useEffect(() => {
    fetchGallery();
  }, [fetchGallery]);

  const handleVote = async (captureId: number, voteType: string) => {
    if (!props.state.isAuthenticated) return;

    const result = await props.postCommands.voteOnGalleryItem(captureId, voteType);
    if (result && result.success) {
      setUserVotes((prev) => {
        const updated = { ...prev };
        if (result.voteType === null) {
          delete updated[captureId];
        } else {
          updated[captureId] = result.voteType;
        }
        return updated;
      });

      setItems((prev) =>
        prev.map((item) => {
          if (item.Id !== captureId) return item;
          const updated = { ...item };
          if (result.action === "added") {
            updated[voteType === "like" ? "like_count" : "dislike_count"] =
              (updated[voteType === "like" ? "like_count" : "dislike_count"] || 0) + 1;
          } else if (result.action === "removed") {
            updated[voteType === "like" ? "like_count" : "dislike_count"] = Math.max(
              (updated[voteType === "like" ? "like_count" : "dislike_count"] || 1) - 1,
              0
            );
          } else if (result.action === "switched") {
            const addKey = voteType === "like" ? "like_count" : "dislike_count";
            const removeKey = voteType === "like" ? "dislike_count" : "like_count";
            updated[addKey] = (updated[addKey] || 0) + 1;
            updated[removeKey] = Math.max((updated[removeKey] || 1) - 1, 0);
          }
          return updated;
        })
      );
    }
  };

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <>
      <SeoHead
        title={t("community.seoTitle")}
        description={t("community.seoDescription")}
        path="/Gallery"
      />
      <Box id="gallery-page" sx={{ maxWidth: 1200, mx: "auto", px: 3, py: 4 }}>
        <Box
          id="gallery-header"
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Typography id="gallery-title" variant="h4" sx={{ fontWeight: 700 }}>
            {t("community.title")}
          </Typography>
          <Box id="gallery-filters" sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>{t("community.sortLabel")}</InputLabel>
              <Select
                value={sort}
                label={t("community.sortLabel")}
                onChange={(e) => {
                  setSort(e.target.value);
                  setPage(1);
                }}
              >
                <MenuItem value="newest">{t("community.sortNewest")}</MenuItem>
                <MenuItem value="popular">{t("community.sortPopular")}</MenuItem>
                <MenuItem value="views">{t("community.sortViews")}</MenuItem>
                <MenuItem value="liked">{t("community.sortLiked")}</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>{t("community.timeLabel")}</InputLabel>
              <Select
                value={timeRange}
                label={t("community.timeLabel")}
                onChange={(e) => {
                  setTimeRange(e.target.value);
                  setPage(1);
                }}
              >
                <MenuItem value="all">{t("community.timeAll")}</MenuItem>
                <MenuItem value="30d">{t("community.time30d")}</MenuItem>
                <MenuItem value="7d">{t("community.time7d")}</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : items.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 8 }}>
            <Typography variant="h6" color="text.secondary">
              {t("community.empty")}
            </Typography>
          </Box>
        ) : (
          <>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                  md: "repeat(3, 1fr)",
                  lg: "repeat(4, 1fr)",
                },
                gap: 2.5,
              }}
            >
              {items.map((item) => (
                <GalleryCard
                  key={item.Id}
                  item={item}
                  userVote={userVotes[item.Id] || null}
                  onVote={handleVote}
                  isAuthenticated={props.state.isAuthenticated}
                  onClick={(id) => setViewingCaptureId(id)}
                />
              ))}
            </Box>

            {totalPages > 1 && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_, value) => setPage(value)}
                  color="primary"
                  size="large"
                />
              </Box>
            )}
          </>
        )}

        {viewingCaptureId && (
          <GalleryViewPopup
            captureId={viewingCaptureId}
            onClose={() => { setViewingCaptureId(null); fetchGallery(); }}
            userVote={userVotes[viewingCaptureId] || null}
            onVote={handleVote}
            isAuthenticated={props.state.isAuthenticated}
            currentUserId={props.state.user?.id}
            postCommands={props.postCommands}
          />
        )}
      </Box>
    </>
  );
};

export default Gallery;
